import { pxToEm } from 'util/pxToEm';
import { Box, Checkbox, Typography } from '@mui/material';
import { Button, Modal, ModalProps, TextInput, ConfirmationModal } from 'components/ui';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { UserSummary, useUsersApi, useSchoolsApi } from 'services/api';
import { Role } from 'hooks/useGetRole';
import DeleteIcon from '@mui/icons-material/Delete';

interface Facility {
  id: string;
  name: string;
  schoolId: string;
  isResidenceHall?: boolean;
  approvers: Array<{
    id: string;
    approverId: string;
    approverName: string;
    approverEmail: string;
    approverPhone: string;
  }>;
}

export interface ManageFacilitiesModalProps
  extends Pick<ModalProps, 'open' | 'onClose'> {
  schoolId?: string;
  isMultiStepApprovalEnabled: boolean;
  facility?: Facility | null; // For edit mode
  mode: 'create' | 'edit';
}

export const ManageFacilitiesModal = ({
  open,
  schoolId,
  onClose,
  isMultiStepApprovalEnabled,
  facility,
  mode,
}: ManageFacilitiesModalProps): ReactElement => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [approvers, setApprovers] = useState<UserSummary[]>([]);
  const [facilityName, setFacilityName] = useState('');
  const [isResidenceHall, setIsResidenceHall] = useState(false);
  const [selectedApproverIds, setSelectedApproverIds] = useState<string[]>([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const usersApi = useUsersApi();
  const schoolsApi = useSchoolsApi();

  const getApprovers = useCallback(async () => {
    if (isMultiStepApprovalEnabled) {
      const roles: Role[] = ['approver'];
      let allApprovers: UserSummary[] = [];
      const pageSize = 50; // Default page size
      let totalCount = 0;

      // First call to get total count
      const firstResult = await usersApi.getUsers({
        page: 1,
        search: '',
        roles,
        showFacilityManagers: true,
      });

      if (!firstResult.success)
        throw new Error(firstResult.error.message);


      totalCount = firstResult.value.count;
      allApprovers = [...firstResult.value.users];

      // Calculate total pages
      const totalPages = Math.ceil(totalCount / pageSize);

      // Loop through remaining pages
      for (let currentPage = 2; currentPage <= totalPages; currentPage++) {
        const result = await usersApi.getUsers({
          page: currentPage,
          search: '',
          roles,
        });

        if (result.success)
          allApprovers = [...allApprovers, ...result.value.users];
         else
          throw new Error(result.error.message);

      }

      setApprovers(allApprovers);
    }
  }, [isMultiStepApprovalEnabled, usersApi]);

    // Initialize form data when facility changes (for edit mode)
  useEffect(() => {
    if (mode === 'edit' && facility) {
      setFacilityName(facility.name);
      setIsResidenceHall(facility.isResidenceHall ?? false);
      setSelectedApproverIds(facility.approvers.map(a => a.approverId));
    } else {
      setFacilityName('');
      setIsResidenceHall(false);
      setSelectedApproverIds([]);
    }
  }, [facility, mode]);

  useEffect(() => {
    if (isMultiStepApprovalEnabled)
      void getApprovers();
  }, [getApprovers]);

  const handleSubmit = async () => {
    if (!facilityName.trim()) {
      enqueueSnackbar('Please enter a facility name', { variant: 'error' });
      return;
    }

    if (!schoolId) {
      enqueueSnackbar('School ID is required', { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      let result;

      if (mode === 'create') {
        result = await schoolsApi.createFacility(schoolId, {
          name: facilityName.trim(),
          approverIds: selectedApproverIds,
          isResidenceHall,
        });
      } else {
        // Edit mode
        if (!facility?.id) {
          enqueueSnackbar('Facility ID is required for editing', { variant: 'error' });
          return;
        }

        result = await schoolsApi.updateFacility(facility.id, {
          name: facilityName.trim(),
          approverIds: selectedApproverIds,
          isResidenceHall,
        });
      }

      if (result.success) {
        const action = mode === 'create' ? 'created' : 'updated';
        enqueueSnackbar(`Facility ${action} successfully`, { variant: 'success' });
        setFacilityName('');
        setIsResidenceHall(false);
        setSelectedApproverIds([]);
        onClose?.();
      } else {
        const action = mode === 'create' ? 'create' : 'update';
        enqueueSnackbar(result.error?.message || `Failed to ${action} facility`, { variant: 'error' });
      }
    } catch (error) {
      const action = mode === 'create' ? 'create' : 'update';
      enqueueSnackbar(`Failed to ${action} facility`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproverToggle = (approverId: string) => {
    setSelectedApproverIds(prev =>
      prev.includes(approverId)
        ? prev.filter(id => id !== approverId)
        : [...prev, approverId],
    );
  };

  const handleDeleteFacility = async () => {
    if (!facility?.id) {
      enqueueSnackbar('Facility ID is required for deletion', { variant: 'error' });
      return;
    }

    setIsDeleting(true);
    try {
      const result = await schoolsApi.deleteFacility(facility.id);

      if (result.success) {
        enqueueSnackbar('Facility deleted successfully', { variant: 'success' });
        setShowDeleteConfirmation(false);
        onClose?.();
      } else {
        enqueueSnackbar(result.error?.message || 'Failed to delete facility', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to delete facility', { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Modal
        title={mode === 'create' ? 'Add Facility' : 'Edit Facility'}
        open={open}
        onClose={onClose}
      >
        <Box sx={{ width: 600, maxWidth: '80vw' }}>
          <Box flex={1} display="flex" justifyContent="space-between" alignItems="start" gap={30}>
            <TextInput
              label="Facility Name"
              width="100%"
              style={{ width: 300 }}
              value={facilityName}
              onChange={e => setFacilityName(e.target.value)}
            />
            {mode === 'edit' && (
            <Box>
              <Box
                display="flex"
                alignItems="center"
                gap={8}
                style={{ cursor: 'pointer' }}
                onClick={() => setShowDeleteConfirmation(true)}
                sx={{
                  color: '#d32f2f',
                  '&:hover': {
                    color: '#b71c1c',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
                <Typography
                  fontSize={pxToEm(14)}
                  sx={{
                    textDecoration: 'underline',
                    color: 'inherit',
                  }}
                >
                  Remove Facility
                </Typography>
              </Box>
            </Box>
          )}
          </Box>

          <Box
            mt={20}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              width: 260,
              height: 48,
              border: '1px solid #DEDEDE',
              borderRadius: '10px',
              px: '16px',
              py: '8px',
              cursor: 'pointer',
            }}
            onClick={() => setIsResidenceHall(prev => !prev)}
          >
            <Typography
              sx={{
                fontSize: pxToEm(14),
                fontWeight: 400,
                color: '#23305F',
              }}
            >
              Residence Hall
            </Typography>
            <Checkbox
              checked={isResidenceHall}
              onClick={e => e.stopPropagation()}
              onChange={() => setIsResidenceHall(prev => !prev)}
            />
          </Box>

          {isMultiStepApprovalEnabled && approvers.length > 0 && (
            <Box mt={30}>
              <Typography fontWeight={500} fontSize={pxToEm(14)}>Assign Approvers</Typography>
              {approvers.map(approver => (
                <Box display="flex" alignItems="center" key={approver.id}>
                  <Checkbox
                    checked={selectedApproverIds.includes(approver.id)}
                    onChange={() => handleApproverToggle(approver.id)}
                  />
                  <Typography variant="body2" component="span">
                    {approver.firstName} {approver.lastName}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box mt={30} display="flex">
          <Box mr={30}>
            <Button minWidth={100} inverted onClick={onClose}>
              Cancel
            </Button>
          </Box>
          <Button
            minWidth={100}
            variant="vibrant"
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </Box>
      </Modal>

      <ConfirmationModal
        title="Confirm Delete"
        open={showDeleteConfirmation}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteFacility}
        message={`Are you sure you want to delete the facility "${facility?.name || 'Unknown'}"?`}
        body={
          <Typography fontSize={pxToEm(14)} color="text.secondary" mt={16}>
            This action cannot be undone.
          </Typography>
        }
        confirmAction={isDeleting ? 'Deleting...' : 'Delete'}
        cancelAction="Cancel"
      />
    </>
  );
};
